/**
 * Universal Schema Form Component
 * Works with any OpenAPI specification to generate forms
 */

import React, { useState, useEffect } from 'react';
import { parseOpenAPISchemaToFields, FieldConfig } from '../../utils/universalSchemaParser';

export interface UniversalSchemaFormProps {
  openApiSpec: any;                    // The OpenAPI specification
  schemaName: string;                  // Which schema to use for the form
  onSubmit: (data: Record<string, any>) => void | Promise<void>;
  initialData?: Record<string, any>;   // Pre-populate form data
  loading?: boolean;                   // Show loading state
  errors?: Record<string, string>;     // Field-level errors
  showSubmitButton?: boolean;          // Whether to show submit button
  submitButtonText?: string;           // Custom submit button text
  formId?: string;                     // Form ID for external submission
  hiddenFields?: string[];             // Fields to hide
  className?: string;                  // Additional CSS classes
  
  // NEW: Customization maps for domain-specific tweaks
  customLabels?: Record<string, string>;        // Override field labels
  customPlaceholders?: Record<string, string>;  // Override field placeholders
  customPatterns?: Record<string, string>;      // Override validation patterns
  customDefaults?: Record<string, any>;         // Override default values
  fieldOrder?: string[];                        // Custom field ordering
}

export function UniversalSchemaForm({
  openApiSpec,
  schemaName,
  onSubmit,
  initialData = {},
  loading = false,
  errors = {},
  showSubmitButton = true,
  submitButtonText = 'Submit',
  formId,
  hiddenFields = [],
  className = '',
  customLabels = {},
  customPlaceholders = {},
  customPatterns = {},
  customDefaults = {},
  fieldOrder = []
}: UniversalSchemaFormProps): JSX.Element {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Generate field configurations from OpenAPI schema
  useEffect(() => {
    try {
      setIsLoading(true);
      setError(null);

      const configs = parseOpenAPISchemaToFields(
        openApiSpec, 
        schemaName, 
        hiddenFields,
        customLabels,
        customPlaceholders,
        customPatterns,
        customDefaults
      );
      
      console.log(`Generated field configs for ${schemaName}:`, configs);
      
      if (configs.length === 0) {
        throw new Error(`No fields found for schema: ${schemaName}`);
      }

      setFieldConfigs(configs);
      
      // Initialize form data with defaults
      const defaultData = { ...initialData };
      configs.forEach(config => {
        if (config.defaultValue !== undefined && defaultData[config.name] === undefined) {
          defaultData[config.name] = config.defaultValue;
        }
      });
      setFormData(defaultData);
      
    } catch (err) {
      console.error('❌ Error generating form fields:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate form fields');
    } finally {
      setIsLoading(false);
    }
  }, [openApiSpec, schemaName, JSON.stringify(hiddenFields)]); // Remove initialData from dependencies

  // Update form data when initialData changes - use JSON.stringify to avoid infinite loops
  useEffect(() => {
    setFormData(prev => ({ ...prev, ...initialData }));
  }, [JSON.stringify(initialData)]);

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Custom validation function
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    let errorCount = 0;
    const maxErrors = 3; // Limit to first 3 errors to avoid overwhelming the user
    
    fieldConfigs.forEach(config => {
      // Stop adding errors if we've reached the limit
      if (errorCount >= maxErrors) return;
      
      const value = formData[config.name];
      const isEmpty = value === undefined || value === null || value === '';
      
      // Required field validation
      if (config.required && isEmpty) {
        errors[config.name] = `${config.label} is required`;
        errorCount++;
        return;
      }
      
      // Skip validation for empty optional fields
      if (isEmpty) return;
      
      // String length validation
      if (typeof value === 'string') {
        if (config.minLength && value.length < config.minLength) {
          errors[config.name] = `${config.label} must be at least ${config.minLength} characters`;
          errorCount++;
          return;
        }
        if (config.maxLength && value.length > config.maxLength) {
          errors[config.name] = `${config.label} must be no more than ${config.maxLength} characters`;
          errorCount++;
          return;
        }
        
        // Pattern validation
        if (config.pattern) {
          const regex = new RegExp(config.pattern);
          if (!regex.test(value)) {
            // Custom error messages for common patterns
            if (config.name === 'commonName') {
              errors[config.name] = 'Common name must be a valid domain format (e.g., api.example.com)';
            } else if (config.name === 'country') {
              errors[config.name] = 'Country code must be 2 uppercase letters (e.g., GB)';
            } else if (config.type === 'email') {
              errors[config.name] = 'Please enter a valid email address';
            } else {
              errors[config.name] = `${config.label} format is invalid`;
            }
            errorCount++;
            return;
          }
        }
      }
      
      // Date validation
      if (config.type === 'datetime' && value) {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          errors[config.name] = `${config.label} must be a valid date`;
          errorCount++;
          return;
        }
        
        // Future date validation for expiry dates
        if (config.name.toLowerCase().includes('expiry') || config.name.toLowerCase().includes('expires')) {
          const now = new Date();
          if (date <= now) {
            errors[config.name] = `${config.label} must be in the future`;
            errorCount++;
            return;
          }
        }
      }
    });
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Run custom validation
    const validationErrors = validateForm();
    setValidationErrors(validationErrors);
    
    // If there are validation errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      // Focus on first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const fieldElement = document.getElementById(`field-${firstErrorField}`);
      if (fieldElement) {
        fieldElement.focus();
      }
      return;
    }
    
    // Form is valid, proceed with submission
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const renderField = (config: FieldConfig) => {
    const fieldValue = formData[config.name] || '';
    const fieldError = validationErrors[config.name] || errors[config.name]; // Use validation errors or external errors
    const fieldId = `field-${config.name}`;

    // Handle hidden fields
    if (config.type === 'hidden') {
      return (
        <input
          key={config.name}
          type="hidden"
          name={config.name}
          value={fieldValue}
        />
      );
    }

    // Handle select fields
    if (config.type === 'select') {
      return (
        <div key={config.name} className="form-group mb-3">
          <label htmlFor={fieldId}>
            {config.label}
            {config.required && <span className="text-danger"> *</span>}
          </label>
          <select
            id={fieldId}
            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
            value={config.multiple ? (Array.isArray(fieldValue) ? fieldValue : []) : fieldValue}
            onChange={(e) => {
              if (config.multiple) {
                const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                handleInputChange(config.name, selectedOptions);
              } else {
                handleInputChange(config.name, e.target.value);
              }
            }}
            multiple={config.multiple}
            disabled={config.disabled || loading}
          >
            {!config.multiple && (
              <option value="">Select {config.label}</option>
            )}
            {config.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {config.helpText && (
            <small className="form-text text-muted">{config.helpText}</small>
          )}
          {fieldError && (
            <div className="invalid-feedback d-block">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {fieldError}
            </div>
          )}
        </div>
      );
    }

    // Handle textarea fields
    if (config.type === 'textarea') {
      return (
        <div key={config.name} className="form-group mb-3">
          <label htmlFor={fieldId}>
            {config.label}
            {config.required && <span className="text-danger"> *</span>}
          </label>
          <textarea
            id={fieldId}
            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
            rows={config.rows || 3}
            value={fieldValue}
            onChange={(e) => handleInputChange(config.name, e.target.value)}
            disabled={config.disabled || loading}
            placeholder={config.placeholder}
          />
          {config.helpText && (
            <small className="form-text text-muted">{config.helpText}</small>
          )}
          {fieldError && (
            <div className="invalid-feedback d-block">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {fieldError}
            </div>
          )}
        </div>
      );
    }

    // Handle file fields
    if (config.type === 'file') {
      return (
        <div key={config.name} className="form-group mb-3">
          <label htmlFor={fieldId}>
            {config.label}
            {config.required && <span className="text-danger"> *</span>}
          </label>
          <input
            id={fieldId}
            type="file"
            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
            onChange={(e) => handleInputChange(config.name, e.target.files?.[0] || null)}
            accept={config.acceptedTypes?.join(',')}
            disabled={config.disabled || loading}
          />
          {config.helpText && (
            <small className="form-text text-muted">{config.helpText}</small>
          )}
          {fieldError && (
            <div className="invalid-feedback d-block">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {fieldError}
            </div>
          )}
        </div>
      );
    }

    // Handle datetime fields (including smart detection of date fields)
    if (config.type === 'datetime' || (config.name.toLowerCase().includes('date') && config.type === 'text')) {
      // Convert datetime value for datetime-local input
      let datetimeValue = fieldValue;
      if (fieldValue && typeof fieldValue === 'string') {
        // If it's an ISO string, convert to datetime-local format
        try {
          const date = new Date(fieldValue);
          if (!isNaN(date.getTime())) {
            // Format as YYYY-MM-DDTHH:MM for datetime-local input
            datetimeValue = date.toISOString().slice(0, 16);
          }
        } catch (e) {
          // If conversion fails, use the original value
          datetimeValue = fieldValue;
        }
      }

      return (
        <div key={config.name} className="form-group mb-3">
          <label htmlFor={fieldId}>
            {config.label}
            {config.required && <span className="text-danger"> *</span>}
          </label>
          <input
            type="datetime-local"
            id={fieldId}
            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
            value={datetimeValue}
            onChange={(e) => {
              // Convert datetime-local value back to ISO string for storage
              const inputValue = e.target.value;
              if (inputValue) {
                // Convert to ISO string for consistent storage
                const isoValue = new Date(inputValue).toISOString();
                handleInputChange(config.name, isoValue);
              } else {
                handleInputChange(config.name, '');
              }
            }}
            disabled={config.disabled || loading}
          />
          {config.helpText && (
            <small className="form-text text-muted">{config.helpText}</small>
          )}
          {fieldError && (
            <div className="invalid-feedback d-block">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {fieldError}
            </div>
          )}
        </div>
      );
    }

    // Handle email fields
    if (config.type === 'email') {
      return (
        <div key={config.name} className="form-group mb-3">
          <label htmlFor={fieldId}>
            {config.label}
            {config.required && <span className="text-danger"> *</span>}
          </label>
          <input
            type="email"
            id={fieldId}
            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
            value={fieldValue}
            onChange={(e) => handleInputChange(config.name, e.target.value)}
            disabled={config.disabled || loading}
            placeholder={config.placeholder}
          />
          {config.helpText && (
            <small className="form-text text-muted">{config.helpText}</small>
          )}
          {fieldError && (
            <div className="invalid-feedback d-block">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {fieldError}
            </div>
          )}
        </div>
      );
    }

    // Handle tel fields
    if (config.type === 'tel') {
      return (
        <div key={config.name} className="form-group mb-3">
          <label htmlFor={fieldId}>
            {config.label}
            {config.required && <span className="text-danger"> *</span>}
          </label>
          <input
            type="tel"
            id={fieldId}
            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
            value={fieldValue}
            onChange={(e) => handleInputChange(config.name, e.target.value)}
            disabled={config.disabled || loading}
            placeholder={config.placeholder}
          />
          {config.helpText && (
            <small className="form-text text-muted">{config.helpText}</small>
          )}
          {fieldError && (
            <div className="invalid-feedback d-block">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {fieldError}
            </div>
          )}
        </div>
      );
    }

    // Default to text input
    return (
      <div key={config.name} className="form-group mb-3">
        <label htmlFor={fieldId}>
          {config.label}
          {config.required && <span className="text-danger"> *</span>}
        </label>
        <input
          type="text"
          id={fieldId}
          className={`form-control ${fieldError ? 'is-invalid' : ''}`}
          value={fieldValue}
          onChange={(e) => handleInputChange(config.name, e.target.value)}
          disabled={config.disabled || loading}
          placeholder={config.placeholder}
        />
        {config.helpText && (
          <small className="form-text text-muted">{config.helpText}</small>
        )}
        {fieldError && (
          <div className="invalid-feedback d-block">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            {fieldError}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading form...</span>
        </div>
        <div className="mt-2">Generating form from schema...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <h6 className="alert-heading">Form Generation Error</h6>
        <p className="mb-0">{error}</p>
        <hr />
        <small className="text-muted">
          Please check that the schema name "{schemaName}" exists in the OpenAPI specification.
        </small>
      </div>
    );
  }

  return (
    <form 
      id={formId}
      onSubmit={handleSubmit} 
      className={`universal-schema-form ${className}`}
      noValidate
    >
      {fieldConfigs.map(renderField)}
      
      {/* Show validation summary if there are errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="alert alert-danger mt-3">
          <h6 className="alert-heading">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please fix the highlighted fields
          </h6>
          <p className="mb-0">
            {Object.keys(validationErrors).length === 3 
              ? "Showing first 3 validation errors. Fix these to see any remaining issues."
              : `${Object.keys(validationErrors).length} field${Object.keys(validationErrors).length > 1 ? 's' : ''} need${Object.keys(validationErrors).length === 1 ? 's' : ''} attention.`
            }
          </p>
        </div>
      )}
      
      {showSubmitButton && (
        <div className="form-group mt-4">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading && (
              <span className="spinner-border spinner-border-sm mr-2" role="status">
                <span className="sr-only">Loading...</span>
              </span>
            )}
            <i className="fas fa-save mr-2"></i>
            {submitButtonText}
          </button>
        </div>
      )}
    </form>
  );
}